import { useEffect, useState } from 'react';
import { api, ApiError } from '../services/api';
import type { Metro } from '../types/metro';

/**
 * Snapshot returned by useMetroData. One of `isLoading | error | metros`
 * is meaningful at any given time — render accordingly.
 */
export interface MetroDataState {
  readonly metros: ReadonlyArray<Metro>;
  readonly isLoading: boolean;
  readonly error: ApiError | null;
  /** Re-trigger the fetch (used by the "Try again" button on error states). */
  readonly retry: () => void;
}

/**
 * Fetch all metros from the API on mount. Cancels in-flight responses on
 * unmount so a fast page change doesn't write stale data into a dead component.
 *
 * Today this is the single read path for the metros dataset. If we add
 * caching/staleness later, swap the body for SWR or React Query without
 * touching the call sites.
 */
export function useMetroData(): MetroDataState {
  const [metros, setMetros] = useState<ReadonlyArray<Metro>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [refetchToken, setRefetchToken] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    api.metros
      .list()
      .then((data) => {
        if (!cancelled) setMetros(data);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof ApiError ? cause : new ApiError('UNKNOWN', String(cause), 0));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refetchToken]);

  return {
    metros,
    isLoading,
    error,
    retry: () => setRefetchToken((t) => t + 1),
  };
}
